import { useAuth } from '@/components/auth/authProvider';
import { useGetOrganizationCoverageAreaSettingsQuery } from '@/services/organizations';
import { Box } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';

import { useGetPendingLeadsQuery } from '../../services/leads';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';

import { ForecastVolumeIndicator } from './ForecastVolumeIndicator';
import { LeadManagementAcceptedTab } from './LeadManagementAcceptedTab';
import { LeadManagementExpiredTab } from './LeadManagementExpiredTab';
import { LeadManagementPendingAcceptanceTab } from './LeadManagementPendingAcceptanceTab';
import { LeadManagementRejectedTab } from './LeadManagementRejectedTab';

const tabLinkStyle = { textDecoration: 'none' } as const;
const tabSpanStyle = { transitionDuration: '0s' } as const;

export function LeadManagement() {
  const { claims } = useAuth();
  const orgAlias = claims?.org as string | undefined;

  const { data: coverageSettings = [] } = useGetOrganizationCoverageAreaSettingsQuery(
    { alias: orgAlias! },
    { skip: !orgAlias },
  );

  const { data: leadsData } = useGetPendingLeadsQuery({});
  const pendingCount = leadsData?.total ?? 0;

  const { allowExceed, monthlyCapacity } = useMemo(() => {
    const year = DateTime.utc().toFormat('yyyy');
    const month = DateTime.utc().toFormat('MM');

    const totalCapacity = coverageSettings.reduce((sum, setting) => {
      return sum + (setting.value.estimates?.[year]?.[month] ?? 0);
    }, 0);

    const allowExceedForecast = coverageSettings[0]?.value.allowExceedForecast ?? false;

    return { allowExceed: allowExceedForecast, monthlyCapacity: totalCapacity };
  }, [coverageSettings]);

  // TODO: forecastPercentage and leadsRemaining require accepted leads count for the current month.
  // Needs a dedicated API endpoint (not yet available). Will be implemented in a follow-up ticket.
  const forecastPercentage = 0;
  const leadsRemaining = monthlyCapacity;

  return (
    <>
      <Helmet>
        <title>Lead Management</title>
      </Helmet>
      <div data-theme="core">
        <MainContainer maxWidth="100">
          <PageHeader title="Lead Management">
            <ForecastVolumeIndicator
              forecastPercentage={forecastPercentage}
              leadsRemaining={leadsRemaining}
              allowExceed={allowExceed}
              orgAlias={orgAlias ?? ''}
            />
          </PageHeader>
          <Box direction="row" borderWidth="0 0 sm 0" borderColor="separator">
            <NavLink to="pending-acceptance" style={tabLinkStyle}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={`tab-span${isActive ? ' tab-active' : ''}`}
                  style={tabSpanStyle}
                  color="body-secondary"
                >
                  {`Pending acceptance · ${pendingCount}`}
                </Box>
              )}
            </NavLink>
            <NavLink to="accepted" style={tabLinkStyle}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={`tab-span${isActive ? ' tab-active' : ''}`}
                  style={tabSpanStyle}
                  color="body-secondary"
                >
                  Accepted
                </Box>
              )}
            </NavLink>
            <NavLink to="rejected" style={tabLinkStyle}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={`tab-span${isActive ? ' tab-active' : ''}`}
                  style={tabSpanStyle}
                  color="body-secondary"
                >
                  Rejected
                </Box>
              )}
            </NavLink>
            <NavLink to="expired" style={tabLinkStyle}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={`tab-span${isActive ? ' tab-active' : ''}`}
                  style={tabSpanStyle}
                  color="body-secondary"
                >
                  Expired
                </Box>
              )}
            </NavLink>
          </Box>
          <Routes>
            <Route path="pending-acceptance" element={<LeadManagementPendingAcceptanceTab />} />
            <Route path="accepted" element={<LeadManagementAcceptedTab />} />
            <Route path="rejected" element={<LeadManagementRejectedTab />} />
            <Route path="expired" element={<LeadManagementExpiredTab />} />
            <Route index element={<Navigate to="pending-acceptance" replace />} />
          </Routes>
        </MainContainer>
      </div>
    </>
  );
}
